import React, { useState, useEffect, useCallback } from 'react'
import { FlyoutMenu, MenuItem, Button, Tooltip, Divider, Chip } from '@dhis2/ui'
import { 
  ChevronDown, User, LogOut, Menu as MenuIcon, ChevronLeft, ChevronRight, 
  Info, Settings, Bell, HelpCircle, Sun, Moon, 
  Edit, Phone
} from 'lucide-react'
import { useNavigate, NavLink, Outlet } from 'react-router-dom'
import {
  LayoutDashboard, Users, Target, NotebookPen, Contact,
  BarChart2, CheckSquare, ListCheck, MessageSquare
} from 'lucide-react'
import { useDataEngine } from '@dhis2/app-runtime'

import { useAuth } from '../../features/auth/AuthContext'
import { SUPERUSER_ROLE_ID, STAFF_ROLES_ID } from './AccessControl'
import { logout } from '../../utils/auth'

// Define navItems outside the component
const navItems = [
  { to: "/dashboard", icon: <LayoutDashboard size={24} />, label: "Dashboard", roles: [] },
  { to: "/performance-management", icon: <NotebookPen size={24} />, label: "Objectives Management", roles: [] },
  { to: "/objectives", icon: <CheckSquare size={24} />, label: "Weekly Tasks Capture", roles: [] },
  { to: "/leadership-management", icon: <NotebookPen />, label: "Leadership Management", roles: [] },
  { to: "/performance-appraisal/mid-year", icon: <Target size={24} />, label: "Mid-Year Appraisal", roles: [] },
  { to: "/performance-appraisal/year-end", icon: <Target size={24} />, label: "Year-End Appraisal", roles: [] },
  { to: "/kpis", icon: <BarChart2 size={24} />, label: "KPIs", roles: [] },
  { to: "/tasks", icon: <ListCheck size={24} />, label: "My Tasks Report", roles: [] },
  { to: "/staff", icon: <Contact size={24} />, label: "Staff Management", roles: [STAFF_ROLES_ID.ADMIN, STAFF_ROLES_ID.CEO] },
  { to: "/registration", icon: <Users size={24} />, label: "Users Management", roles: [SUPERUSER_ROLE_ID] },
  { to: "/settings", icon: <Settings size={24} />, label: "System Settings", roles: [STAFF_ROLES_ID.ADMIN, STAFF_ROLES_ID.CEO] },
]

// Program configuration (same as in LeadershipManagementPage)
const PROGRAM_CONFIG = {
  program: 'NkReudUAW4F',
  stages: {
    fiscalYear: 'htIWgNQWEMm' // The stage for fiscal year metadata
  },
  dataElements: {
    fiscalYearStart: 'U55MqwBcRAe',
    fiscalYearEnd: 'kgdk4NXu4f5',
    companyLogo: 'muysgcGfkyN'
  }
}

const Layout = () => {
  const { user, registrationComplete } = useAuth()
  const navigate = useNavigate()
  const engine = useDataEngine()

  const [menuOpen, setMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile)
  const [darkMode, setDarkMode] = useState(false)
  const [notificationCount] = useState(3) // Mock notification count
  const [fiscalYear, setFiscalYear] = useState({ start: null, end: null })
  const [loading, setLoading] = useState(true)

  const [companyLogo, setCompanyLogo] = useState(null) 

  // Fetch fiscal year dates - exactly as in LeadershipManagementPage
  useEffect(() => {
    const loadFiscalYear = async () => {
      if (!user?.organisationUnits?.[0]?.id || !engine) {
        setLoading(false)
        return
      }

      try {
        console.log('🔍 Loading fiscal year for orgUnit:', user.organisationUnits[0].id)

        // Get Staff TEI from enrollments (search ALL accessible OUs)
        const enrollmentsResponse = await engine.query({
          enrollmentsQuery: {
            resource: 'enrollments',
            params: {
              ouMode: 'ACCESSIBLE',
              program: PROGRAM_CONFIG.program,
              fields: 'trackedEntityInstance',
              pageSize: 1,
              order: 'lastUpdated:desc'
            }
          }
        })

        const teiId = enrollmentsResponse.enrollmentsQuery?.enrollments?.[0]?.trackedEntityInstance
        if (!teiId) {
          console.warn('⚠️ No Staff TEI found for fiscal year load')
          setLoading(false)
          return
        }

        console.log('📋 Found TEI for fiscal year:', teiId)

        // Query metadata event (search ALL accessible OUs)
        const eventsResponse = await engine.query({
          eventsQuery: {
            resource: 'events',
            params: {
              program: PROGRAM_CONFIG.program,
              programStage: PROGRAM_CONFIG.stages.fiscalYear,
              ouMode: 'ACCESSIBLE',
              trackedEntityInstance: teiId,
              pageSize: 1,
              order: 'eventDate:desc',
              fields: 'dataValues[dataElement,value]'
            }
          }
        })

      const event = eventsResponse.eventsQuery?.events?.[0]
            let start = null, end = null, logo = null

            if (event) {
              event.dataValues?.forEach(dv => {
                if (dv.dataElement === PROGRAM_CONFIG.dataElements.fiscalYearStart) start = dv.value
                if (dv.dataElement === PROGRAM_CONFIG.dataElements.fiscalYearEnd) end = dv.value
                if (dv.dataElement === PROGRAM_CONFIG.dataElements.companyLogo) logo = dv.value
              })
            }

            if (start && end) {
              setFiscalYear({ start, end })
              console.log('✅ Fiscal year loaded from metadata:', { start, end })
            } else {
              console.log('ℹ️ No fiscal year dates found in metadata event')
            }
         if (logo) {
          console.log('🖼️ Found logo base64 string')
          // For base64, we can use the string directly in img src
          setCompanyLogo(logo)
        }
          } catch (error) {
            console.error('❌ Failed to load fiscal year:', error)
          } finally {
            setLoading(false)
          }
        }

        loadFiscalYear()
      }, [user?.organisationUnits?.[0]?.id, engine])

  // Check if current date is within a date range
  const isWithinDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return false

    const now = new Date()
    const start = new Date(startDate)
    const end = new Date(endDate)

    return now >= start && now <= end
  }

  // Calculate mid-year period (first 6 months of fiscal year)
  const getMidYearPeriod = useCallback(() => {
    if (!fiscalYear.start) return { start: null, end: null }

    const start = new Date(fiscalYear.start)
    const end = new Date(start)
    end.setMonth(end.getMonth() + 6)

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    }
  }, [fiscalYear.start])

  // Calculate end-year period (remaining 6 months of fiscal year)
  const getEndYearPeriod = useCallback(() => {
    if (!fiscalYear.start || !fiscalYear.end) return { start: null, end: null }

    const midYearEnd = new Date(fiscalYear.start)
    midYearEnd.setMonth(midYearEnd.getMonth() + 6)

    return {
      start: midYearEnd.toISOString().split('T')[0],
      end: fiscalYear.end
    }
  }, [fiscalYear.start, fiscalYear.end])

  // Check if mid-year appraisal should be visible
  const showMidYearAppraisal = useCallback(() => {
    if (loading) return false
    if (!fiscalYear.start) return false

    const midYear = getMidYearPeriod()
    return isWithinDateRange(midYear.start, midYear.end)
  }, [loading, fiscalYear.start, getMidYearPeriod])

  // Check if year-end appraisal should be visible
  const showYearEndAppraisal = useCallback(() => {
    if (loading) return false
    if (!fiscalYear.start || !fiscalYear.end) return false

    const endYear = getEndYearPeriod()
    return isWithinDateRange(endYear.start, endYear.end)
  }, [loading, fiscalYear.start, fiscalYear.end, getEndYearPeriod])

  // Filter nav items based on roles AND date conditions
  const getFilteredNavItems = useCallback(() => {
    return navItems.filter(item => {
      // First check role-based access
      if (item.roles.length > 0) {
        if (!user?.authorities) return false
        if (!item.roles.some(r => user.authorities.includes(r))) return false
      }

      // Then check date-based conditions for appraisal items
      if (item.to === "/performance-appraisal/mid-year") {
        return showMidYearAppraisal()
      }

      if (item.to === "/performance-appraisal/year-end") {
        return showYearEndAppraisal()
      }

      // For all other items, return true if role check passed
      return true
    })
  }, [user, showMidYearAppraisal, showYearEndAppraisal])

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (mobile) setSidebarOpen(false)
      if (!mobile) setSidebarOpen(true)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

    // Clean up object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (companyLogo && companyLogo.startsWith('blob:')) {
        URL.revokeObjectURL(companyLogo)
      }
    }
  }, [companyLogo])
  

  const toggleDarkMode = () => {
    setDarkMode(!darkMode)
    // Add your dark mode implementation here
  }

  // Helper function to convert role ID to role name
  const getRoleName = (roleId) => {
    if (!roleId) return 'User'

    // Role mapping based on your STAFF_ROLES_ID constants
    const roleMap = {
      // System Admin Roles
      'Ufph3mGRmMo': 'System Administrator', // Superuser
      'pjJxqLRaMvL': 'System Administrator', // System Administrator

      // Data Management Roles
      'h4ZasD6YxCr': 'Data Entry Clerk',
      'Fd1c7pUqNy4': 'Data Manager',
      'OUWmJBx0x2V': 'Data Analyst',
      'iKmPvYEwiUu': 'Reports Analyst',

      // HR Management Roles
      'f7b4XrCnVlH': 'HR Manager',
      'gtMJNK0QVL7': 'Top Staff', // Production Director level
      'vdVjNaqxblC': 'Senior Staff', // Shift Manager level
      'nLSSLch316I': 'Chief Executive Officer',
      'adeqQmxfCLF': 'Head of Department',

      // Facility Management
      'yLq8bQm9vR2': 'Factory Manager',
      'pQm4zRtKvL9': 'Cold Room Supervisor',

      // Other common roles
      'xYz9aBcDeFg': 'Viewer', // Read-only access
      'aBcDeFgHiJk': 'Approver',
      'lMnOpQrStUv': 'Validator'
    }

    return roleMap[roleId] || roleId.replace('ROLE_', '').replace(/_/g, ' ')
  }

  const linkStyle = {
    color: 'white',
    textDecoration: 'none',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px',
    whiteSpace: 'nowrap',
    borderRadius: '6px'
  }

  const activeLinkStyle = {
    color: '#0ae315ff',
    backgroundColor: 'rgba(255,204,0,0.15)'
  }

  const handleNavClick = () => {
    if (isMobile) setSidebarOpen(false)
  }

  // Custom styles for enhanced menu
  const menuStyles = {
    menuContainer: {
      minWidth: '320px',
      maxWidth: '400px',
    },
    userSection: {
      padding: '16px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
    },
    userName: {
      margin: '0 0 4px 0',
      fontSize: '18px',
      fontWeight: 600,
    },
    userEmail: {
      margin: 0,
      fontSize: '14px',
      opacity: 0.9,
    },
    infoField: {
      marginBottom: '8px',
    },
    fieldLabel: {
      fontSize: '11px',
      opacity: 0.8,
      marginBottom: '2px',
    },
    fieldValue: {
      fontSize: '13px',
      fontWeight: 500,
    },
    badge: {
      backgroundColor: '#ef4444',
      color: 'white',
      fontSize: '11px',
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: '12px',
      marginLeft: '8px',
    }
  }

  // Log for debugging
  useEffect(() => {
    if (!loading && fiscalYear.start && fiscalYear.end) {
      const midYear = getMidYearPeriod()
      const endYear = getEndYearPeriod()
      console.log('📅 Fiscal Year:', fiscalYear)
      console.log('📅 Mid-Year Period:', midYear)
      console.log('📅 End-Year Period:', endYear)
      console.log('📅 Show Mid-Year:', showMidYearAppraisal())
      console.log('📅 Show End-Year:', showYearEndAppraisal())
      console.log('📅 Current Date:', new Date().toISOString().split('T')[0])
    }
  }, [loading, fiscalYear, getMidYearPeriod, getEndYearPeriod, showMidYearAppraisal, showYearEndAppraisal])

  // Rest of your component remains the same...
  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'sans-serif' }}>
      {/* Sidebar */}
      <aside
        style={{
          width: sidebarOpen ? '250px' : '60px',
          background: '#003366',
          color: 'white',
          padding: sidebarOpen ? '1rem' : '0.5rem',
          display: isMobile ? (sidebarOpen ? 'flex' : 'none') : 'flex',
          flexDirection: 'column',
          gap: '1rem',
          position: isMobile ? 'fixed' : 'relative',
          height: '100%',
          zIndex: 300,
          transition: 'width 0.3s ease, padding 0.3s ease',
          boxShadow: isMobile && sidebarOpen ? '2px 0 12px rgba(0,0,0,0.4)' : 'none',
          overflowX: 'hidden',
        }}
      >
        {/* Header with Logo or Icon */}
         <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarOpen ? 'space-between' : 'center',
            marginBottom: sidebarOpen ? '16px' : '0',
            minHeight: '60px'
          }}
        >
          {sidebarOpen ? (
            <>
              {companyLogo ? (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  width: '100%',
                  justifyContent: 'space-between'
                }}>
                  <img 
                    src={companyLogo} 
                    alt="Company Logo" 
                    style={{ 
                      maxHeight: '60px',  // Increased from 40px to 60px
                      maxWidth: '180px',  // Increased from 150px to 180px
                      width: 'auto',
                      objectFit: 'contain',
                      backgroundColor: 'white',
                      padding: '6px',      // Slightly increased padding
                      borderRadius: '6px',  // Slightly increased border radius
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onError={(e) => {
                      console.error('Logo failed to load:', companyLogo)
                      e.target.onerror = null // Prevent infinite loop
                      e.target.style.display = 'none'
                      // Show fallback text
                      const parent = e.target.parentNode
                      if (parent) {
                        const fallback = document.createElement('span')
                        fallback.style.fontWeight = 'bold'
                        fallback.style.fontSize = '18px'  // Increased from 16px
                        fallback.style.color = 'white'
                        fallback.textContent = 'Company'
                        parent.insertBefore(fallback, e.target)
                      }
                    }}
                  />
                  <Button
                    small
                    icon={<ChevronLeft size={18} />}
                    onClick={() => setSidebarOpen(false)}
                    style={{ background: 'transparent', border: 'none', color: 'white' }}
                    title="Collapse Sidebar"
                  />
                </div>
              ) : (
                <>
                  <span style={{ fontWeight: 'bold', fontSize: '18px', color: 'white' }}>Company</span>
                  <Button
                    small
                    icon={<ChevronLeft size={18} />}
                    onClick={() => setSidebarOpen(false)}
                    style={{ background: 'transparent', border: 'none', color: 'white' }}
                    title="Collapse Sidebar"
                  />
                </>
              )}
            </>
          ) : (
            <Button
              small
              icon={<ChevronRight size={18} />}
              onClick={() => setSidebarOpen(true)}
              style={{ background: 'transparent', border: 'none', color: 'white', padding: '8px' }}
              title="Expand Sidebar"
            />
          )}
        </div>

        {/* Navigation Items */}
        {registrationComplete &&
          getFilteredNavItems().map(({ to, icon, label }) => {
            if (!sidebarOpen) {
              return (
                <Tooltip dense content={label} key={to} placement="right">
                  <NavLink
                    to={to}
                    style={({ isActive }) => ({
                      ...linkStyle,
                      justifyContent: 'center',
                      ...(isActive ? activeLinkStyle : {}),
                      paddingLeft: 0,
                      paddingRight: 0,
                    })}
                    onClick={handleNavClick}
                  >
                    {icon}
                  </NavLink>
                </Tooltip>
              )
            }
            return (
              <NavLink
                key={to}
                to={to}
                style={({ isActive }) => ({
                  ...linkStyle,
                  ...(isActive ? activeLinkStyle : {}),
                })}
                onClick={handleNavClick}
              >
                {icon}
                {label}
              </NavLink>
            )
          })}

        {/* Show loading indicator while fetching fiscal year */}
        {loading && sidebarOpen && (
          <div style={{ color: 'white', padding: '8px', fontSize: '12px' }}>
            Loading calendar...
          </div>
        )}
      </aside>

      {/* Content area */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          marginLeft: 0,
          transition: 'margin-left 0.3s ease',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <header
          style={{
            background: '#f4f4f4',
            padding: '1rem',
            position: 'sticky',
            top: 0,
            borderBottom: '1px solid #ddd',
            zIndex: 100,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Left side with hamburger and mobile logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Hamburger icon for mobile */}
              {isMobile && (
                <Button
                  small
                  icon={<MenuIcon size={20} />}
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  style={{ marginRight: '4px' }}
                />
              )}

            {/* Mobile logo - visible only when sidebar is hidden */}
              {isMobile && !sidebarOpen && companyLogo && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '40px',
                  maxWidth: '120px'
                }}>
                  <img 
                    src={companyLogo} 
                    alt="Company Logo" 
                    style={{ 
                      maxHeight: '40px',
                      maxWidth: '120px',
                      width: 'auto',
                      objectFit: 'contain',
                      backgroundColor: 'white',
                      padding: '4px',
                      borderRadius: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.style.display = 'none'
                    }}
                  />
                </div>
              )}

            {/* Page title - only show when sidebar is open or on desktop */}
            {(!isMobile || sidebarOpen) && (
              <h1 style={{ margin: 0, fontSize: '20px', color: 'green' }}>
                Performance Management System
              </h1>
           )}
               
          </div>

          {/* User menu - keep existing user menu code exactly as is */}
          <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '12px',
              minWidth: '150px',
              position: 'relative',
            }}>
            {/* Notifications bell (optional) */}
            <button
              onClick={() => navigate('/notifications')}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                position: 'relative',
                padding: '8px',
              }}
            >
              <Bell size={20} color="#666" />
              {notificationCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  background: '#ef4444',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {notificationCount}
                </span>
              )}
            </button>

            {/* User menu button */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setMenuOpen(prev => !prev)}
                style={{
                  display: 'flex',
                  backgroundColor: '#078b14ff',
                  alignItems: 'center',
                  gap: '6px',
                  border: '1px solid #ddd',
                  borderRadius: '22px',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  color: 'white',
                  boxShadow: '0 8px 12px rgba(0,0,0,0.3)',
                  minWidth: '120px',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <User size={20} />
                  <span style={{ fontSize: '14px' }}>{user?.name?.split(' ')[0] || 'User'}</span>
                </div>
                <ChevronDown size={16} style={{ 
                  transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }} />
              </button>

              {menuOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  zIndex: 200,
                  minWidth: '320px',
                }}>
                  <FlyoutMenu style={{ ...menuStyles.menuContainer, width: '320px' }}>
                    {/* User Info Section */}
                    <div style={menuStyles.userSection}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: 'white',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}>
                          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h3 style={menuStyles.userName}>{user?.name || 'User'}</h3>
                          <p style={menuStyles.userEmail}>
                            {user?.email || 'No email set'}
                          </p>
                        </div>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(1, 1fr)',
                        gap: '8px',
                        marginBottom: '16px'
                      }}>
                        <div style={menuStyles.infoField}>
                          <div style={menuStyles.fieldValue}>
                            {user?.organisationUnits?.[0]?.name || 'Not assigned'}
                          </div>
                        </div>

                        <div style={menuStyles.infoField}>
                          <div style={menuStyles.fieldValue}>
                            <Chip dense small>
                              {getRoleName(user?.userCredentials?.userRoles?.[0]?.id) || 'User'}
                            </Chip>
                          </div>
                        </div>

                        {user?.userCredentials?.username && (
                          <div style={menuStyles.infoField}>
                            <div style={menuStyles.fieldLabel}>Username</div>
                            <div style={menuStyles.fieldValue}>
                              <code style={{ fontSize: '11px' }}>{user.userCredentials.username}</code>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <Divider />

                    {/* Profile Section - 2 items per row */}
                    <div style={{ padding: '8px 0' }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '4px'
                      }}>
                        <MenuItem
                          label="Edit"
                          icon={<Edit size={16} />}
                          onClick={() => {
                            navigate('/profile')
                            setMenuOpen(false)
                          }}
                          dense
                          style={{ textAlign: 'center', justifyContent: 'center' }}
                        />
                        {user?.phoneNumber && (
                          <MenuItem
                            label={user.phoneNumber}
                            icon={<Phone size={16} />}
                            dense
                            style={{ 
                              textAlign: 'left', 
                              justifyContent: 'flex-start',
                              padding: '8px 12px',
                              color: '#374151'
                            }}
                            onClick={() => {
                              navigator.clipboard.writeText(user.phoneNumber);
                              alert(`Phone number copied to clipboard: ${user.phoneNumber}`);
                            }}
                          />
                        )}
                      </div>
                    </div>

                    <Divider />

                    {/* Quick Access - 2 items per row */}
                    <div style={{ padding: '8px 0' }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '4px'
                      }}>
                        <MenuItem
                          label={
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center', 
                              width: '100%',
                              position: 'relative'
                            }}>
                              <span>Notifications</span>
                              {notificationCount > 0 && (
                                <span style={menuStyles.badge}>
                                  {notificationCount}
                                </span>
                              )}
                            </div>
                          }
                          icon={<Bell size={16} />}
                          onClick={() => {
                            navigate('/notifications')
                            setMenuOpen(false)
                          }}
                          dense
                          style={{ textAlign: 'center', justifyContent: 'center' }}
                        />

                        <MenuItem
                          label="Messages"
                          icon={<MessageSquare size={16} />}
                          onClick={() => {
                            navigate('/s')
                            setMenuOpen(false)
                          }}
                          dense
                          style={{ textAlign: 'center', justifyContent: 'center' }}
                        />
                      </div>
                    </div>

                    <Divider />

                    {/* Settings & Theme - 2 items per row */}
                    <div style={{ padding: '8px 0' }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '4px'
                      }}>
                        <MenuItem
                          label="Settings"
                          icon={<Settings size={16} />}
                          onClick={() => {
                            navigate('/settings')
                            setMenuOpen(false)
                          }}
                          dense
                          style={{ textAlign: 'center', justifyContent: 'center' }}
                        />

                        <MenuItem
                          label={darkMode ? 'Light Mode' : 'Dark Mode'}
                          icon={darkMode ? <Sun size={16} /> : <Moon size={16} />}
                          onClick={() => {
                            toggleDarkMode()
                            setMenuOpen(false)
                          }}
                          dense
                          style={{ textAlign: 'center', justifyContent: 'center' }}
                        />
                      </div>
                    </div>

                    <Divider />

                    {/* Help & Support - Full width */}
                    <div style={{ padding: '8px 0' }}>
                      <MenuItem
                        label="Help & Support"
                        icon={<HelpCircle size={16} />}
                        onClick={() => {
                          window.open('https://pms.kakobrands.com/PMS_END_USER_MANUAL.pdf', '_blank')
                          setMenuOpen(false)
                        }}
                        dense
                      />
                    </div>

                    <Divider />

                    {/* Logout - Full width with improved styling */}
                    <div style={{ padding: '8px 0' }}>
                      <MenuItem
                        label={
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            width: '100%',
                            color: '#ef4444'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <LogOut size={16} />
                              <span>Sign Out</span>
                            </div>
                            <span style={{ fontSize: '12px', color: '#999' }}>
                              {user?.name?.split(' ')[0] || 'User'}
                            </span>
                          </div>
                        }
                        onClick={() => {
                          setMenuOpen(false)
                          logout(navigate)
                        }}
                        dense
                        style={{ 
                          color: '#ef4444',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          borderRadius: '6px',
                          marginTop: '4px'
                        }}
                      />
                    </div>

                    {/* Footer - 2 items per row */}
                    <div style={{
                      padding: '12px 16px',
                      fontSize: '11px',
                      color: '#666',
                      borderTop: '1px solid #eee',
                      marginTop: '8px',
                    }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '8px',
                        alignItems: 'center'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#10b981',
                            animation: 'pulse 2s infinite'
                          }}></div>
                          <span>Online</span>
                        </div>

                        <div style={{ textAlign: 'right' }}>
                          <div>v1.1.4</div>
                          <div>© 2025 PMS</div>
                        </div>
                      </div>
                    </div>
                  </FlyoutMenu>
                </div>
              )}
            </div>

            {/* Help/Info button */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => window.open('https://pms.kakobrands.com/PMS_END_USER_MANUAL.pdf', '_blank')}
                style={{
                  display: 'flex',
                  backgroundColor: '#e9a20aff',
                  alignItems: 'center',
                  gap: '6px',
                  border: '1px solid #ddd',
                  borderRadius: '22px',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  color: 'white',
                  boxShadow: '0 8px 12px rgba(0,0,0,0.3)',
                }}
              >
                <Info size={24} />
              </button>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main
          style={{
            padding: registrationComplete ? '2rem' : '0',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default Layout